/**
 * Adapted from https://github.com/reach/router/blob/b60e6dd781d5d3a4bdaaf4de665649c0f6a7e78d/src/lib/history.js
 *
 * https://github.com/reach/router/blob/master/LICENSE
 * */
import "./utils";
function getLocation(source) {
    return Object.assign(Object.assign({}, source.location), { state: source.history.state });
}
function createHistory(source) {
    const listeners = [];
    let location = getLocation(source);
    let previousURL = null;
    return {
        get location() {
            return location;
        },
        listen(listener) {
            listeners.push(listener);
            const popstateListener = () => {
                location = getLocation(source);
                previousURL = null;
                listener({ location, action: "POP" });
            };
            source.addEventListener("popstate", popstateListener);
            return () => {
                source.removeEventListener("popstate", popstateListener);
                const index = listeners.indexOf(listener);
                listeners.splice(index, 1);
            };
        },
        navigate(to, { state, replace = false } = {}) {
            state = Object.assign(Object.assign({}, state), { key: Date.now() + "" });
            // try...catch iOS Safari limits to 100 pushState calls
            try {
                if (replace) {
                    source.history.replaceState(state, null, to);
                }
                else {
                    previousURL = Object.assign({}, source.location);
                    source.history.pushState(state, null, to);
                }
            }
            catch (e) {
                if (replace) {
                    if (source.location.replace)
                        source.location.replace(to);
                }
                else {
                    if (source.location.assign)
                        source.location.assign(to);
                }
            }
            location = getLocation(source);
            listeners.forEach(listener => listener({ location, action: "PUSH" }));
        },
        /// Navigate to the given URL, but go back one step in the history if we just came from that page.
        navigateBack(to, { state } = {}) {
            if (previousURL === null) {
                navigate(to, { state });
                return;
            }
            // Use the URL object to resolve the path relative to where we are now
            const prevURL = new URL("http://dummy.com" + previousURL.pathname + previousURL.search);
            const currentURL = new URL("http://dummy.com" + location.pathname + location.search);
            let toURL = new URL(to, currentURL);
            if (prevURL.toString() === toURL.toString()) {
                source.history.back();
            }
            else {
                navigate(to, { state });
            }
        }
    };
}
// Stores history entries in memory for testing or other platforms like Native
function createMemorySource(initialPathname = "/") {
    let index = 0;
    const stack = [{ pathname: initialPathname, search: "" }];
    const states = [null];
    return {
        get location() {
            return stack[index];
        },
        addEventListener(name, fn) { },
        removeEventListener(name, fn) { },
        history: {
            get entries() {
                return stack;
            },
            get index() {
                return index;
            },
            get state() {
                return states[index];
            },
            back() {
                index--;
                stack.pop();
                states.pop();
            },
            pushState(state, _, uri) {
                const [pathname, search = ""] = uri.split("?");
                index++;
                stack.push({ pathname, search });
                states.push(state);
            },
            replaceState(state, _, uri) {
                const [pathname, search = ""] = uri.split("?");
                stack[index] = { pathname, search };
                states[index] = state;
            }
        }
    };
}
// Global history uses window.history as the source if available,
// otherwise a memory history
const canUseDOM = Boolean(typeof window !== "undefined" &&
    window.document &&
    window.document.createElement);
const globalHistory = createHistory(canUseDOM ? window : createMemorySource());
const { navigate, navigateBack } = globalHistory;
export { globalHistory, navigate, navigateBack, createHistory, createMemorySource };
